import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import {
  Box,
  FormControl,
  Typography,
  FormLabel,
  TextField,
  Button,
  Select,
  MenuItem,
  Modal,
  DialogContent,
  DialogActions,
} from "@mui/material";
import th from "dayjs/locale/th";
import { FormattedMessage } from "react-intl";
import { exerciseActivityTypeService } from "../../../services/master-data.service";
import {
  deleteCommunityEventService,
  patchCommunityEventService,
} from "../../../services/community.service";
import { modalStyle } from "../../../consts/styles";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { OverwriteAdapterDayjs } from "../../../utils/common.util";
import SuccessModal from "../SuccessModal";
import ErrorModal from "../ErrorModal";
import dayjs from "dayjs";
import ConfirmModal from "../ConfirmModal";

const minDate = new Date(new Date().getTime() + 86400000);

const EditEventModal = ({
  eventData,
  handleEditEventClose,
  handleEditedEventClose,
}: any) => {
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [openSuccess, setSuccessOpen] = useState<boolean>(false);
  const [openError, setErrorOpen] = useState<boolean>(false);
  const [openWarning, setWarningOpen] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const [exerciseActivityTypes, setExerciseActivityTypes] = useState([]);
  const handleSuccessOpen = () => setSuccessOpen(true);
  const handleSuccessClose = () => {
    setSuccessOpen(false);
    handleEditedEventClose();
  };
  const handleWarningOpen = () => setWarningOpen(true);
  const handleWarningClose = () => setWarningOpen(false);
  const handleErrorOpen = () => setErrorOpen(true);
  const handleErrorClose = () => {
    setErrorMessage("");
    setErrorOpen(false);
  };
  const handleLoading = () => setLoading(true);
  const handleLoaded = () => setLoading(false);

  const { register, formState, setValue, handleSubmit, trigger } = useForm({
    defaultValues: {
      event: eventData.event,
      activity_type_id: eventData.activity_type_id
        ? eventData.activity_type_id
        : -1,
      start_date: eventData.start_date,
      end_date: eventData.end_date,
      result_count_type: eventData.result_count_type,
    },
  });

  const { isValid } = formState;

  const onSubmitHandler: SubmitHandler<any> = async (values) => {
    try {
      handleLoading();
      await patchCommunityEventService(eventData.event_id, {
        ...values,
        activity_type_id:
          values.activity_type_id === -1 ? null : values.activity_type_id,
        start_date: startDate,
        end_date: endDate,
      });
      handleSuccessOpen();
    } catch (error: any) {
      const resMessage =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      setErrorMessage(resMessage);
      handleLoaded();
    }
  };

  const onDeleteEvent = async () => {
    try {
      handleLoading();
      await deleteCommunityEventService(eventData.event_id);
      handleEditedEventClose();
    } catch (error: any) {
      const resMessage =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      setErrorMessage(resMessage);
      handleLoaded();
    }
  };

  const getExerciseActivityType = () => {
    exerciseActivityTypeService().then((resp) => {
      setExerciseActivityTypes(resp.data);
    });
  };

  const setEventForm = () => {
    setStartDate(eventData.start_date);
    setEndDate(eventData.end_date);
  };

  useEffect(getExerciseActivityType, []);

  useEffect(() => {
    if (errorMessage) handleErrorOpen();
  }, [errorMessage]);

  useEffect(setEventForm, [eventData]);

  return (
    <>
      <DialogContent sx={{ width: { xs: 300, sm: 600 } }}>
        <Box display="flex" flexDirection="column">
          <Box component="a" onClick={handleEditEventClose} alignSelf="end">
            <CancelOutlinedIcon
              sx={{ cursor: "pointer", color: "lightGrey.main" }}
            />
          </Box>

          <Typography align="center" typography="h3" color="green.main">
            แก้ไขกิจกรรม
          </Typography>

          <Box display="flex" flexDirection="column" gap="16px">
            <FormControl fullWidth>
              <FormLabel>
                <Typography variant="body1" color="common.black">
                  ชื่อกิจกรรม
                </Typography>
              </FormLabel>

              <TextField
                placeholder="กรอกชื่อกิจกรรม"
                {...register("event", { required: true, maxLength: 100 })}
              />
            </FormControl>

            <FormControl fullWidth>
              <FormLabel>
                <Typography variant="body1" color="common.black">
                  วันเริ่ม
                </Typography>
              </FormLabel>

              <LocalizationProvider
                dateAdapter={OverwriteAdapterDayjs}
                adapterLocale={th}
              >
                <DatePicker
                  minDate={minDate}
                  views={["year", "month", "day"]}
                  inputFormat="DD/MMM/BBBB"
                  value={startDate}
                  InputProps={{
                    disableUnderline: true,
                  }}
                  onChange={async (newValue: any) => {
                    const date = dayjs(newValue).format("YYYY-MM-DD");
                    await trigger("start_date", { shouldFocus: true });
                    setStartDate(date);
                    setValue("start_date", date);
                    setEndDate(null);
                    setValue("end_date", "");
                  }}
                  renderInput={(params: any) => (
                    <TextField
                      {...params}
                      inputProps={{
                        ...params.inputProps,
                        placeholder: "เลือกวันเริ่ม",
                        readOnly: true,
                      }}
                      {...register("start_date", { required: true })}
                      error={false}
                    />
                  )}
                />
              </LocalizationProvider>
            </FormControl>

            <FormControl fullWidth>
              <FormLabel>
                <Typography variant="body1" color="common.black">
                  วันสิ้นสุด
                </Typography>
              </FormLabel>

              <LocalizationProvider
                dateAdapter={OverwriteAdapterDayjs}
                adapterLocale={th}
              >
                <DatePicker
                  minDate={dayjs(startDate).add(1, "day")}
                  views={["year", "month", "day"]}
                  inputFormat="DD/MMM/BBBB"
                  value={endDate}
                  onChange={async (newValue: any) => {
                    const date = dayjs(newValue).format("YYYY-MM-DD");
                    await trigger("end_date", { shouldFocus: true });
                    setEndDate(date);
                    setValue("end_date", date);
                  }}
                  renderInput={(params: any) => (
                    <TextField
                      {...params}
                      inputProps={{
                        ...params.inputProps,
                        placeholder: "เลือกวันสิ้นสุด",
                        readOnly: true,
                      }}
                      {...register("end_date", { required: true })}
                    />
                  )}
                />
              </LocalizationProvider>
            </FormControl>

            <FormControl fullWidth>
              <FormLabel>
                <Typography variant="body1" color="common.black">
                  ประเภทกีฬา
                </Typography>
              </FormLabel>

              <Select
                defaultValue={eventData.activity_type_id || -1}
                {...register("activity_type_id")}
                sx={{
                  "& .MuiSelect-select .notranslate::after": "เลือกประเภทกีฬา"
                    ? {
                        content: `"เลือกประเภทกีฬา"`,
                        opacity: 0.42,
                      }
                    : {},
                }}
              >
                <MenuItem value={-1}>ทุกประเภทกีฬา</MenuItem>
                {exerciseActivityTypes.map((item: any, index) => (
                  <MenuItem value={item.id} key={index}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <FormLabel>
                <Typography variant="body1" color="common.black">
                  การนับผล
                </Typography>
              </FormLabel>

              <Select
                defaultValue={eventData.result_count_type}
                {...register("result_count_type", { required: true })}
                sx={{
                  "& .MuiSelect-select .notranslate::after": "เลือกการนับผล "
                    ? {
                        content: `"เลือกการนับผล"`,
                        opacity: 0.42,
                      }
                    : {},
                }}
              >
                <MenuItem value={1} key={0}>
                  ระยะทาง
                </MenuItem>

                <MenuItem value={2} key={1}>
                  ระยะเวลา
                </MenuItem>

                <MenuItem value={3} key={2}>
                  แคลอรี
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          padding: "0px 20px 24px",
        }}
      >
        <Button
          fullWidth
          color="silver"
          variant="contained"
          startIcon={<DeleteForeverOutlinedIcon />}
          sx={{ boxShadow: "none" }}
          onClick={handleWarningOpen}
        >
          <Typography typography="subtitle2" fontWeight={600}>
            ลบกิจกรรม
          </Typography>
        </Button>

        <Box display="flex" gap="8px" width={1}>
          <Button
            fullWidth
            color="blue"
            variant="outlined"
            onClick={handleEditEventClose}
          >
            <Typography variant="subtitle2">ยกเลิก</Typography>
          </Button>

          <Button
            fullWidth
            color="green"
            variant="contained"
            onClick={handleSubmit(onSubmitHandler)}
            disabled={!isValid || loading}
          >
            <Typography variant="subtitle2" color="white">
              <FormattedMessage id="common.confirm" />
            </Typography>
          </Button>
        </Box>
      </DialogActions>

      <Modal keepMounted open={openSuccess}>
        <Box sx={modalStyle} width="340px">
          <SuccessModal
            message={"แก้ไขกิจกรรมสำเร็จ"}
            handleSuccessClose={handleSuccessClose}
            showButton={true}
          />
        </Box>
      </Modal>

      <Modal keepMounted open={openError} onClose={handleErrorClose}>
        <Box sx={modalStyle} width="340px">
          <ErrorModal
            message={errorMessage}
            handleErrorClose={handleErrorClose}
          />
        </Box>
      </Modal>

      <Modal keepMounted open={openWarning} onClose={handleWarningClose}>
        <Box sx={modalStyle} width="340px">
          <ConfirmModal
            message={"คุณแน่ใจหรือไม่ ว่าจะลบกิจกรรมนี้"}
            handleConfirm={onDeleteEvent}
            handleClose={handleWarningClose}
          />
        </Box>
      </Modal>
    </>
  );
};

export default EditEventModal;
